import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';

/**
 * Where the app shell registers its listener for failed requests.
 *
 * A module-level callback rather than a context, because the QueryClient is
 * constructed once at import time — long before any React tree exists — and
 * because the whole point is to report a failure from anywhere, including a
 * query whose component has already unmounted.
 */
let reporter = null;

export function onQueryFailure(fn) {
  reporter = fn;

  return () => {
    if (reporter === fn) reporter = null;
  };
}

function report(error, meta = {}) {
  // A caller can opt out: background polling, or a 404 it treats as an answer.
  if (meta?.silent) return;

  const status = error?.response?.status;

  /*
   * 401 is not worth reporting. The axios interceptor already logs the user out
   * and redirects, so a toast would flash on the way to the login screen saying
   * something they can do nothing about.
   *
   * 403 and 404 are usually a deliberate answer to a specific question — the
   * admin console's status probe, an invite token that does not exist — and the
   * screens that ask them handle it locally.
   */
  if ([401, 403, 404].includes(status)) return;

  reporter?.({
    message:
      error?.response?.data?.message ||
      (status
        ? 'Something did not load. Please try again.'
        : 'We could not reach the server — check your connection.'),
    status,
  });
}

/**
 * The query client.
 *
 * THE PROBLEM THIS SOLVES
 *
 * The audit found 69 of 71 queries rendering no error state. Combined with
 * `retry: 1` and the `data?.data || []` idiom used everywhere, a failed request
 * became `undefined`, then `[]`, then an empty state — so the app told people
 * "No posts yet" when what had actually happened was that the request failed.
 *
 * That is the worst failure mode available: indistinguishable from success with
 * no data, so nobody reports it, and the people most likely to hit it are the
 * ones on a bad connection who already doubt the product.
 *
 * Fixing 69 call sites individually is worth doing and is being done — but a
 * cache-level handler fixes all of them at once, and keeps fixing the ones
 * written next year. The inline treatment (see components/QueryError) is the
 * better experience where it exists; this is the floor underneath it.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => report(error, query?.meta),
  }),

  /*
   * Mutations too. A failed mutation usually has a local handler — the button
   * that started it is right there — but "usually" is doing a lot of work in
   * that sentence, and a save that silently does nothing is its own kind of bad.
   */
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation?.options?.onError) return;   // handled at the call site

      report(error, mutation?.meta);
    },
  }),

  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,
    },
  },
});

export default queryClient;
